import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, ChevronUp, ChevronDown, Copy, Lock, User } from 'lucide-react';

// Mục việc khách lẻ (có khachId) → bấm nhảy sang QL Khách lẻ mở đúng khách đó
export const NutKhach = ({ khachId }) => {
  const navigate = useNavigate();
  return (
    <button type="button" onClick={() => navigate(`/khach-le?khach=${khachId}`)} title="Mở khách này ở QL Khách lẻ"
      className="flex-shrink-0 flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[10px] font-bold text-fuchsia-800 bg-fuchsia-50 border border-fuchsia-200">
      <User size={11} /> Khách
    </button>
  );
};

/**
 * Checklist component for daily report — manages todo items with add, remove, toggle, reorder.
 * Props: items, onChange, accent, onCopyPrevious (optional)
 */
const DailyReportChecklist = ({ items, onChange, accent, onCopyPrevious }) => {
  const [newItemText, setNewItemText] = React.useState('');
  const itemRefs = useRef([]);

  const addItem = () => {
    const t = newItemText.trim();
    if (!t) return;
    onChange([...items, { text: t, done: false }]);
    setNewItemText('');
  };

  const insertItemAfter = (idx) => {
    const n = [...items];
    n.splice(idx + 1, 0, { text: '', done: false });
    onChange(n);
    setTimeout(() => itemRefs.current[idx + 1]?.focus(), 50);
  };

  const toggleItem = (i) => onChange(items.map((it, idx) => idx === i ? { ...it, done: !it.done } : it));
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  const updateText = (i, text) => onChange(items.map((it, idx) => idx === i ? { ...it, text } : it));
  const moveItem = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const n = [...items];
    [n[i], n[j]] = [n[j], n[i]];
    onChange(n);
  };

  const userItems = items.filter(it => !it.isSystem);
  const systemItems = items.filter(it => it.isSystem);
  const doneCount = items.filter(it => it.done).length;
  const totalCount = items.length;
  const pct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header with progress */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100">
        <span className="text-sm font-bold text-gray-700">Công việc hôm nay</span>
        {totalCount > 0 && (
          <div className="ml-auto flex items-center gap-2">
            <div className="w-14 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: pct === 100 ? '#16a34a' : accent }} />
            </div>
            <span className="text-xs font-bold" style={{ color: pct === 100 ? '#16a34a' : accent }}>{doneCount}/{totalCount}</span>
          </div>
        )}
      </div>

      {/* Item list — user tasks */}
      <div className="px-2 py-1.5 space-y-0.5">
        {userItems.map((item, idx) => (
          <div key={idx} className="flex items-center gap-1 py-1.5 px-1.5 rounded-lg group transition-colors" style={{ background: item.done ? '#f0fdf4' : 'transparent' }}>
            <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-black text-white flex-shrink-0" style={{ background: item.done ? '#16a34a' : accent }}>{idx + 1}</span>
            <button type="button" onClick={() => toggleItem(items.indexOf(item))} className="flex-shrink-0 rounded border-2 flex items-center justify-center transition-all"
              style={{ width: 18, height: 18, borderColor: item.done ? '#16a34a' : '#9ca3af', background: item.done ? '#16a34a' : 'transparent' }}>
              {item.done && <span className="text-white text-[9px] font-bold">✓</span>}
            </button>
            <input type="text" value={item.text} onChange={(e) => updateText(items.indexOf(item), e.target.value)}
              ref={(el) => { itemRefs.current[idx] = el; }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); insertItemAfter(items.indexOf(item)); } }}
              className={`flex-1 py-1 px-2 text-sm rounded-md focus:outline-none focus:ring-1 ${item.done ? 'text-green-700 font-medium bg-green-50/50' : 'text-gray-800 bg-gray-50'}`}
              style={{ border: '1px solid #d1d5db' }} placeholder="Nhập nội dung..." />
            {item.khachId && <NutKhach khachId={item.khachId} />}
            <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
              <button type="button" onClick={() => moveItem(items.indexOf(item), -1)} className="text-gray-400 hover:text-gray-600 p-0 leading-none" disabled={idx === 0}><ChevronUp size={12} /></button>
              <button type="button" onClick={() => moveItem(items.indexOf(item), 1)} className="text-gray-400 hover:text-gray-600 p-0 leading-none" disabled={idx === userItems.length - 1}><ChevronDown size={12} /></button>
            </div>
            <button type="button" onClick={() => removeItem(items.indexOf(item))} className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-all flex-shrink-0 p-0.5"><Trash2 size={12} /></button>
          </div>
        ))}

        {/* System tasks — locked, non-editable */}
        {systemItems.map((item, idx) => (
          <div key={'sys-' + idx} className="flex items-center gap-1 py-1.5 px-1.5 rounded-lg bg-emerald-50 border border-emerald-100">
            <span className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 bg-emerald-500">
              <span className="text-white text-[9px] font-black">✓</span>
            </span>
            <div className="w-[18px] h-[18px] rounded border-2 flex items-center justify-center flex-shrink-0 border-emerald-400 bg-emerald-400">
              <span className="text-white text-[9px] font-bold">✓</span>
            </div>
            <span className="flex-1 text-sm text-emerald-700 font-medium px-2">{item.text}</span>
            <span className="text-[9px] font-bold text-emerald-500 bg-emerald-100 px-1.5 py-0.5 rounded-full flex-shrink-0">HT</span>
            <Lock size={11} className="text-emerald-400 flex-shrink-0" />
          </div>
        ))}

        {/* New item input */}
        <div className="flex items-center gap-1 py-1.5 px-1.5">
          <span className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-gray-400 border-2 border-dashed border-gray-300 flex-shrink-0">{items.length + 1}</span>
          <div style={{ width: 18 }} className="flex-shrink-0" />
          <input type="text" value={newItemText} onChange={(e) => setNewItemText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addItem(); } }}
            placeholder="Nhập việc mới, nhấn Enter..."
            className="flex-1 py-1.5 px-2 text-sm rounded-md focus:outline-none focus:ring-1 focus:ring-blue-300 placeholder-gray-400"
            style={{ border: '1px solid #9ca3af', background: '#f9fafb' }} />
          <button type="button" onClick={addItem} className="w-7 h-7 rounded-lg text-white flex items-center justify-center flex-shrink-0 active:scale-90 transition-transform" style={{ background: accent }}><Plus size={14} /></button>
        </div>
      </div>

      {/* Copy from previous */}
      {onCopyPrevious && (
        <div className="px-3 pb-2.5 pt-1">
          <button type="button" onClick={onCopyPrevious} className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold border border-dashed transition-colors hover:bg-gray-50"
            style={{ borderColor: accent + '60', color: accent }}>
            <Copy size={12} /> Lấy việc ngày trước
          </button>
        </div>
      )}
    </div>
  );
};

export default DailyReportChecklist;
